import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CreditCard, BarChart3, Plus, Download } from "lucide-react";
import api, { apiErr } from "../lib/api";
import { SponsoredCard } from "../components/SponsoredCard";
import { FileUpload } from "../components/FileUpload";

const CATEGORIES = ["Tech & AI Trends", "Creative Business", "Design & Visual Arts", "Innovation & Startups", "Lifestyle & Culture", "Inspiration & People", "Gaming", "Digital Culture"];

const STATUS_LABEL = {
  draft: ["Draft", "bg-gray-100 text-gray-700"],
  pending_approval: ["Paid — Pending Approval", "bg-yellow-100 text-yellow-800"],
  scheduled: ["Scheduled / Live", "bg-green-100 text-green-800"],
  needs_revision: ["Needs Revision", "bg-orange-100 text-orange-800"],
  rejected: ["Rejected", "bg-red-100 text-red-700"],
  refunded: ["Refunded", "bg-gray-200 text-gray-700"],
};

export default function AdvertiserDashboard() {
  const [tab, setTab] = useState("campaigns");
  const [packages, setPackages] = useState([]);
  const [campaigns, setCampaigns] = useState([]);

  const load = () => api.get("/advertiser/campaigns").then((r) => setCampaigns(r.data)).catch(() => {});
  useEffect(() => { api.get("/packages").then((r) => setPackages(r.data)).catch(() => {}); load(); }, []);

  return (
    <div className="pti-container py-10" data-testid="advertiser-dashboard">
      <h1 className="font-display text-4xl font-black">Advertiser Dashboard</h1>
      <div className="flex gap-5 mt-5 border-b border-gray-200" role="tablist">
        {["campaigns", "create"].map((t) => (
          <button key={t} onClick={() => setTab(t)} role="tab" aria-selected={tab === t} data-testid={`ad-tab-${t}`}
            className={`text-sm font-bold uppercase tracking-wider pb-2 border-b-2 transition-colors ${tab === t ? "border-pti-red text-pti-black" : "border-transparent text-gray-500"}`}>{t}</button>
        ))}
      </div>

      {tab === "create" ? <CampaignBuilder packages={packages} onSaved={() => { load(); setTab("campaigns"); }} />
        : <CampaignList campaigns={campaigns} reload={load} onCreate={() => setTab("create")} />}
    </div>
  );
}

function CampaignBuilder({ packages, onSaved }) {
  const [f, setF] = useState({ package: "discovery", brand_name: "", logo: "", headline: "", description: "", destination_url: "", cta: "Learn more", image: "", video: "", category: CATEGORIES[0], start_date: "", end_date: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const pkg = packages.find((p) => p.lookup_key === f.package);

  const saveDraft = async () => {
    try { await api.post("/advertiser/campaigns", f); toast.success("Draft saved."); onSaved(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const createAndCheckout = async (provider) => {
    if (!f.brand_name || !f.headline || !f.description || !f.destination_url) return toast.error("Please complete brand name, headline, description and destination URL.");
    try {
      const { data } = await api.post("/advertiser/campaigns", f);
      const endpoint = provider === "crypto" ? "/payments/bitpay/checkout" : "/payments/checkout";
      const { data: co } = await api.post(endpoint, { campaign_id: data.id, origin_url: window.location.origin });
      if (co.invoice_id) localStorage.setItem("pti_bitpay_invoice", co.invoice_id);
      window.location.href = co.checkout_url;
    } catch (e) { toast.error(apiErr(e)); }
  };

  const previewAd = { id: null, brand_name: f.brand_name || "Your Brand", logo: f.logo, headline: f.headline || "Your headline goes here", description: f.description || "A short, human description of your product or offer.", cta: f.cta, destination_url: f.destination_url || "#", image: f.image, category: f.category };

  return (
    <div className="mt-8 grid lg:grid-cols-2 gap-10" data-testid="campaign-builder">
      <div className="space-y-4">
        <div>
          <label className="text-sm font-bold">1. Choose a package</label>
          <div className="grid sm:grid-cols-2 gap-2 mt-2">
            {packages.map((p) => (
              <button key={p.lookup_key} onClick={() => setF({ ...f, package: p.lookup_key })} data-testid={`pkg-${p.lookup_key}`}
                className={`text-left p-3 border transition-colors ${f.package === p.lookup_key ? "border-pti-red bg-red-50" : "border-gray-200 hover:border-gray-400"}`}>
                <div className="font-bold text-sm">{p.name}</div>
                <div className="text-pti-red font-display font-bold">${p.price}{p.interval ? "/mo" : ""}</div>
                <div className="text-[11px] text-gray-500">{p.placement}</div>
              </button>
            ))}
          </div>
        </div>
        <div><label className="text-sm font-bold">2. Category / audience</label>
          <select value={f.category} onChange={set("category")} data-testid="camp-category" className="w-full border border-gray-300 px-3 py-2.5 mt-1 outline-none focus:border-pti-red">{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-sm font-bold">Start date</label><input type="date" value={f.start_date} onChange={set("start_date")} data-testid="camp-start" className="w-full border border-gray-300 px-3 py-2.5 mt-1 outline-none focus:border-pti-red" /></div>
          <div><label className="text-sm font-bold">End date</label><input type="date" value={f.end_date} onChange={set("end_date")} data-testid="camp-end" className="w-full border border-gray-300 px-3 py-2.5 mt-1 outline-none focus:border-pti-red" /></div>
        </div>
        <div className="border-t border-gray-200 pt-3"><label className="text-sm font-bold">3. Build your native ad</label></div>
        <In label="Brand name" v={f.brand_name} on={set("brand_name")} t="camp-brand" />
        <In label="Logo URL (optional)" v={f.logo} on={set("logo")} t="camp-logo" />
        <In label="Headline" v={f.headline} on={set("headline")} t="camp-headline" />
        <div><label className="text-sm font-bold">Short description</label><textarea value={f.description} onChange={set("description")} rows={3} data-testid="camp-desc" className="w-full border border-gray-300 p-3 mt-1 outline-none focus:border-pti-red" /></div>
        <In label="Destination URL" v={f.destination_url} on={set("destination_url")} t="camp-url" />
        <In label="Call-to-action text" v={f.cta} on={set("cta")} t="camp-cta" />
        <FileUpload label="Upload image (optional)" accept="image/*" testid="camp-image-upload" onUploaded={(url) => setF((p) => ({ ...p, image: url }))} hint="Or paste a URL below." />
        <In label="Image URL (optional)" v={f.image} on={set("image")} t="camp-image" />
        <FileUpload label="Upload vertical video (optional)" accept="video/*" testid="camp-video-upload" onUploaded={(url) => setF((p) => ({ ...p, video: url }))} hint="Or paste a URL below." />
        <In label="Vertical video URL (optional)" v={f.video} on={set("video")} t="camp-video" />
        <p className="text-xs text-gray-500">No HTML, scripts, or third-party ad code needed — we build a responsive native ad for you.</p>
        <div className="text-xs text-gray-600 border-l-2 border-pti-red pl-3">
          You'll be charged the exact USD price shown. Refunds are handled per PictureThisInk's advertising-refund policy. Your campaign stays <strong>Payment Pending</strong> until confirmed, then <strong>Paid — Pending Approval</strong> until an admin approves it.
        </div>
        <div className="flex flex-wrap gap-3">
          <button onClick={saveDraft} data-testid="camp-save" className="border border-pti-black px-5 py-2.5 font-bold hover:bg-gray-100 transition-colors">Save draft</button>
          <button onClick={() => createAndCheckout("card")} data-testid="camp-pay" className="flex items-center gap-2 bg-pti-red text-white px-5 py-2.5 font-bold hover:bg-pti-redhover transition-colors"><CreditCard size={16} /> Pay by Card ${pkg?.price}{pkg?.interval ? "/mo" : ""}</button>
        </div>
        <p className="text-[11px] text-gray-500">Payments are processed securely by Stripe. Campaigns run only after payment confirmation and PictureThisInk approval.</p>
      </div>

      <div className="lg:sticky lg:top-28 self-start">
        <div className="text-sm font-bold mb-2">Live preview</div>
        <div className="max-w-sm" data-testid="ad-preview"><SponsoredCard ad={previewAd} label="Sponsored" /></div>
        <div className="mt-4 bg-gray-50 border border-gray-200 p-4 text-sm">
          <div className="font-bold">{pkg?.name}</div>
          <div className="text-gray-600">{pkg?.tagline}</div>
          <div className="mt-2 font-display text-2xl font-bold text-pti-red">${pkg?.price}{pkg?.interval ? "/month" : ` · ${pkg?.duration_days} days`}</div>
          <p className="text-xs text-gray-500 mt-2">You'll pay securely via Stripe. After payment your status becomes "Paid — Pending Approval" and goes live once an admin approves it.</p>
        </div>
      </div>
    </div>
  );
}

function CampaignList({ campaigns, reload, onCreate }) {
  const invoice = (c) => {
    const txt = `PictureThisInk Invoice\n\nBrand: ${c.brand_name}\nPackage: ${c.package}\nAmount: $${c.price}\nStatus: ${c.payment_status}\nCampaign ID: ${c.id}\nDate: ${new Date().toLocaleDateString()}\n`;
    const blob = new Blob([txt], { type: "text/plain" });
    const url = URL.createObjectURL(blob); const a = document.createElement("a");
    a.href = url; a.download = `invoice-${c.id.slice(0, 8)}.txt`; a.click(); URL.revokeObjectURL(url);
  };
  return (
    <div className="mt-8" data-testid="campaign-list">
      <button onClick={onCreate} className="flex items-center gap-2 bg-pti-red text-white px-5 py-2.5 font-bold hover:bg-pti-redhover transition-colors mb-6"><Plus size={16} /> New campaign</button>
      {campaigns.length === 0 && <p className="text-gray-400">No campaigns yet. Create your first native ad.</p>}
      <div className="space-y-4">
        {campaigns.map((c) => {
          const [label, cls] = STATUS_LABEL[c.status] || [c.status, "bg-gray-100"];
          const ctr = c.impressions ? ((c.clicks / c.impressions) * 100).toFixed(1) : "0.0";
          return (
            <div key={c.id} className="border border-gray-200 p-5" data-testid={`camp-row-${c.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="font-display text-lg font-bold">{c.headline}</div>
                  <div className="text-sm text-gray-500">{c.brand_name} · {c.package} · ${c.price}</div>
                </div>
                <span className={`text-[11px] font-bold px-2 py-1 ${cls}`}>{label}</span>
              </div>
              <div className="grid grid-cols-4 gap-3 mt-4 text-center">
                {[["Impressions", c.impressions], ["Clicks", c.clicks], ["CTR", `${ctr}%`], ["Spend", `$${c.status === "scheduled" || c.payment_status === "paid" ? c.price : 0}`]].map(([l, v]) => (
                  <div key={l}><div className="font-bold">{v}</div><div className="text-[10px] uppercase text-gray-500">{l}</div></div>
                ))}
              </div>
              <div className="flex gap-3 mt-3">
                {c.payment_status === "paid" && <button onClick={() => invoice(c)} data-testid={`invoice-${c.id}`} className="flex items-center gap-1 text-xs font-bold text-pti-red"><Download size={12} /> Invoice</button>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function In({ label, v, on, t }) {
  return <div><label className="text-sm font-bold">{label}</label><input value={v} onChange={on} data-testid={t} className="w-full border border-gray-300 px-3 py-2.5 mt-1 outline-none focus:border-pti-red" /></div>;
}
